import { Col, Input, Row, Select, Space, Typography } from 'antd';
import { debounce } from 'lodash';
import { Search } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { contactStatusOptions } from '../../constants';
import { getContacts } from '../../redux/actions/contact';
import { getCustomers } from '../../redux/actions/customer';
import UserSearchSelect from '../UserSearchSelect';

const { Option } = Select;

const ContactFilters = ({ onSearchChange, search, onFilterChange }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { customers } = useSelector((state) => state.customersSlice);
    const [loading, setLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isSearched, setIsSearched] = useState(false);
    const status = Number(searchParams.get('status') ?? 1);

    const handleCustomerSearch = useCallback(
        debounce(async (value) => {
            setIsSearched(!!value);
            setLoading(true);
            getCustomers({ search: value, loading: false });
            setLoading(false);
        }, 300),
        []
    );

    const handleStatusFilter = (statusValue) => {
        getContacts({ status: statusValue, netsuite_customer_id: selectedCustomer, assigned_user_id: selectedUser, search });

        const params = new URLSearchParams(searchParams);
        params.set('status', statusValue);
        setSearchParams(params);

        onFilterChange?.({
            netsuite_customer_id: selectedCustomer,
            assigned_user_id: selectedUser,
            status: statusValue,
        });
    };

    const handleFilter = ({ netsuite_customer_id, assigned_user_id }) => {
        getContacts({
            netsuite_customer_id,
            assigned_user_id,
            search
        });

        onFilterChange?.({
            netsuite_customer_id,
            assigned_user_id,
            status,
        });
    };

    return (
        <Row gutter={[10, 10]}>
            <Col xs={24} sm={12} md={8} lg={12}>
                <Input
                    placeholder="Search..."
                    size="large"
                    allowClear
                    prefix={<Search size={18} color="rgb(156,163,175)" />}
                    value={search}
                    onChange={onSearchChange}
                />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
                <Select
                    placeholder="Status"
                    style={{ width: '100%' }}
                    size="large"
                    defaultValue={1}
                    options={contactStatusOptions?.map((status) => {
                        return {
                            ...status,
                            value: status.key,
                        };
                    })}
                    value={status}
                    onChange={(val) => handleStatusFilter(val)}
                />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
                <Select
                    placeholder="Search Customer"
                    optionLabelProp="label"
                    style={{ width: '100%' }}
                    size="large"
                    loading={loading}
                    showSearch
                    allowClear
                    value={selectedCustomer}
                    onChange={(value) => {
                        setSelectedCustomer(value);
                        handleFilter({ netsuite_customer_id: value, assigned_user_id: selectedUser });
                    }}
                    filterOption={(input, option) =>
                        String(option['data-name'] || '')
                            .toLowerCase()
                            .includes(input.toLowerCase()) ||
                        String(option['data-code'] || '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                    onSearch={handleCustomerSearch}
                    onClear={() => getCustomers({ loading: false })}
                    notFoundContent={isSearched ? 'No Results' : null}
                >
                    {isSearched &&
                        customers?.map((customer) => {
                            return (
                                <Option
                                    key={customer?.id}
                                    value={customer?.netsuite_customer_id}
                                    label={
                                        <Space>
                                            <Typography.Text strong>({customer?.code})</Typography.Text>
                                            <Typography.Text>{customer?.name}</Typography.Text>
                                        </Space>
                                    }
                                    data-code={customer?.code}
                                    data-name={customer?.name}
                                >
                                    <Space>
                                        <Typography.Text strong>({customer?.code})</Typography.Text>
                                        <Typography.Text>{customer?.name}</Typography.Text>
                                    </Space>
                                </Option>
                            );
                        })}
                </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
                <UserSearchSelect
                    value={selectedUser}
                    onChange={(assigned_user_id) => {
                        setSelectedUser(assigned_user_id);
                        handleFilter({
                            netsuite_customer_id: selectedCustomer,
                            assigned_user_id,
                        })
                    }
                    }
                />
            </Col>
        </Row>
    );
};

export default ContactFilters;
